import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Tooltip from 'flarum/common/components/Tooltip';
import Button from 'flarum/common/components/Button';
import Checkbox from 'flarum/common/components/Checkbox';
import Stream from 'flarum/common/utils/Stream';
import ItemList from 'flarum/common/utils/ItemList';
import classList from 'flarum/common/utils/classList';
import { debounce } from 'flarum/common/utils/throttleDebounce';

import ApplicationHistoryModal from '../components/ApplicationHistoryModal';

export default class EditUserApplicationsModal extends Modal {
    oninit(vnode) {
        super.oninit(vnode);

        this.user = this.attrs.user;
        this.onsuccess = this.attrs.onsuccess ?? (() => {});
        this.applications = {};
        this.isLoadingPage = true;

        app.store.all('applications').filter((application) => application.canView()).forEach((application) => {
            this.applications[application.id()] = {
                active: Stream(false),
                subscribedUntil: Stream(dayjs().format('YYYY-MM-DDTHH:mm:ss')),
                application: application
            };
        });

        // load user with serialized applications
        app.store.find('users', this.user?.id()).then((user) => {
            user.applications().filter((application) => application.canView()).forEach((application) => {
                this.applications[application.id()].active(true);
                this.applications[application.id()].subscribedUntil(dayjs(application.subscribedUntil()).format('YYYY-MM-DDTHH:mm:ss'));
            })
            this.isLoadingPage = false;
            m.redraw();
        });
    }

    className() {
        return 'EditUserApplicationsModal Modal--large';
    }

    title() {
        return app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.label');
    }

    content() {
        const applications = Object.values(this.applications).map((data) => data.application);

        const columns = this.columns().toArray();

        return (
            <div className="Modal-body">
                <div className="Form">
                    <div className="EditUserApplicationsModal">
                        <section
                            className={classList(['EditUserApplicationsModal-grid', this.isLoadingPage ? 'EditUserApplicationsModal-grid--loadingPage' : 'EditUserApplicationsModal-grid--loaded'])}
                            style={{ '--columns': columns.length }}
                            role="table"
                            // +1 to account for header
                            aria-rowcount={applications.length + 1}
                            aria-colcount={columns.length}
                            aria-live="polite"
                            aria-busy={this.isLoadingPage ? 'true' : 'false'}
                        >
                            {/* Render columns */}
                            {
                                columns.map((column, colIndex) => (
                                    <div className="EditUserApplicationsModal-grid-header" role="columnheader" aria-colindex={colIndex + 1} aria-rowindex={1}>
                                        {column.name}
                                    </div>
                                ))
                            }
                            {/* Render application data */}
                            {
                                applications.map((application, rowIndex) =>
                                    columns.map((col, colIndex) => {
                                        const columnContent = col.content && col.content(application), columnTooltip = { tooltipVisible: col.tooltip?undefined:false, ...(col.tooltip && col.tooltip(application)) };
                                        return (
                                            <Tooltip { ...columnTooltip }>
                                                <div
                                                    className={classList(['EditUserApplicationsModal-grid-rowItem', (rowIndex % 2 >= applications.length % 2) && 'EditUserApplicationsModal-grid-rowItem--shaded'])}
                                                    data-application-id={application.id()}
                                                    data-column-name={col.itemName}
                                                    aria-colindex={colIndex + 1}
                                                    // +2 to account for 0-based index, and for the header row
                                                    aria-rowindex={rowIndex + 2}
                                                    role="cell"
                                                >
                                                    {columnContent}
                                                </div>
                                            </Tooltip>
                                        );
                                    })
                                )
                            }
                            {/* Loading spinner that shows when a new page is being loaded */}
                            {this.isLoadingPage && <LoadingIndicator size='medium' />}
                        </section>
                        <Button className="Button Button--primary" loading={this.loading} type="submit">
                            { app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.save_button') }
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    columns() {
        const columns = new ItemList();

        columns.add(
            'active',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.active'),
                content: (application) => {
                    return (
                        <Checkbox
                            disabled={application.suspendedAt()||application.status().application=='frozen'||!application.canEdit()}
                            state={this.applications[application.id()].active()}
                            onchange={this.applications[application.id()].active}
                        />
                    );
                }
            },
            100
        );

        columns.add(
            'name',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.name'),
                content: (application) => application.name()
            },
            90
        );

        columns.add(
            'subscribed_until',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.subscribed_until'),
                content: (application) => {
                    return (
                        <input
                            type="datetime-local"
                            name="subscribed_until"
                            disabled={!this.applications[application.id()].active()||application.suspendedAt()||application.status().application=='frozen'||!application.canEdit()}
                            bidi={this.applications[application.id()].subscribedUntil}
                            min={dayjs(this.applications[application.id()].subscribedUntil()).subtract(1, 'month').format('YYYY-MM-DDTHH:mm:ss')}
                            max={dayjs(this.applications[application.id()].subscribedUntil()).add(1, 'month').format('YYYY-MM-DDTHH:mm:ss')}
                            step="1"
                            required
                        />
                    );
                }
            },
            80
        );

        columns.add(
            'license',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.license'),
                content: (application) => (
                    <Button
                        id={'license-'+application.id()}
                        className="Button Button--transparent EditUserApplicationsList-editModalBtn"
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.reset')}
                        icon='fab fa-digital-ocean'
                        disabled={!this.applications[application.id()].active()||!application.canEdit()}
                        onclick={() => {
                            this.$(`#license-${application.id()}`).attr('disabled', true);
                            application.save(
                                { license: null },
                                { params: { user: this.user.id() }, errorHandler: this.onerror.bind(this) }
                            ).then(() => {
                                this.$(`#license-${application.id()}`).removeAttr('disabled');
                                let alert = app.alerts.show({ dismissible: true, type: 'success' }, app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.success_reset'));
                                debounce(2500, () => app.alerts.dismiss(alert))();
                                m.redraw();
                            });
                        }}
                    >
                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.reset')}
                    </Button>
                ),
                tooltip: (application) => ({ text: application.license()?.substr(0, 16) ?? '', position: 'left', tooltipVisible: application.license() ? undefined : false })
            },
            -80
        );

        columns.add(
            'suspend',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.suspend'),
                content: (application) => (
                    <Button
                        id={'suspend-'+application.id()}
                        className="Button Button--transparent EditUserApplicationsList-editModalBtn"
                        title={application.suspendedAt() ? app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.unsuspend_button') : app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.suspend_button')}
                        icon='fas fa-ban'
                        disabled={!this.applications[application.id()].active()||application.status().application=='frozen'||!application.canEdit()}
                        onclick={() => {
                            this.$(`#suspend-${application.id()}`).attr('disabled', true);
                            application.save(
                                { suspended_at: application.suspendedAt() ? null : dayjs().toISOString(), subscribed_until: dayjs(this.applications[application.id()].subscribedUntil()).toISOString() },
                                { params: { user: this.user.id() }, errorHandler: this.onerror.bind(this) }
                            ).then((payload) => {
                                this.$(`#suspend-${application.id()}`).removeAttr('disabled');
                                this.applications[application.id()].subscribedUntil = Stream(dayjs(payload?.subscribedUntil()).format('YYYY-MM-DDTHH:mm:ss'));
                                m.redraw();
                            });
                        }}
                    >
                        {application.suspendedAt() ? app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.unsuspend_button') : app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.suspend_button')}
                    </Button>
                ),
                tooltip: (application) => ({ text: application.suspendedAt() ? dayjs(application.suspendedAt()).format('LLL') : '', position: 'left', tooltipVisible: application.suspendedAt() ? undefined : false })
            },
            -90
        );

        columns.add(
            'history',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.history.label'),
                content: (application) => (
                    <Button
                        className="Button Button--transparent EditUserApplicationsList-editModalBtn"
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.open')}
                        icon='fas fa-history'
                        onclick={() => {
                            app.modal.show(ApplicationHistoryModal, { user: this.user, application: application }, true);
                        }}
                        disabled={!application.canViewHistories()}
                    >
                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.grid.open')}
                    </Button>
                )
            },
            -100
        );

        return columns;
    }

    onsubmit(e) {
        e.preventDefault();

        this.loading = true;

        const active_applications = Object.values(this.applications)
            .filter((data) => data.active())
            .map((data) => data.application);

        this.user.save(
            { relationships: { applications: active_applications } },
            { errorHandler: this.onerror.bind(this) }
        ).then(() => {
            Promise.all(active_applications.filter((application) => application.canEdit()).map((application) => {
                // force saving
                application.exists = true;
                // save application
                const id = application.id();
                return application.save(
                    { subscribed_until: dayjs(this.applications[id].subscribedUntil()).toISOString() },
                    { url: `/api/applications/${id}?user=${this.user.id()}`, errorHandler: this.onerror.bind(this) }
                );
            })).then(() => {
                this.onsuccess();
                this.hide();
            }, this.loaded.bind(this));
        }).catch(() => {
            this.loading = false;
            m.redraw();
        });
    }
}
